import { useNotifications } from "@/hooks/use-notifications";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";
import type { SelectNotification } from "@shared/schema";
import {
  Bell,
  UserPlus,
  Users,
  MapPin,
  Flame,
  Moon,
  ThumbsUp,
  Trash2,
  CheckCheck,
} from "lucide-react";

function relativeTime(input: string | Date | null): string {
  if (!input) return "";
  const then = new Date(input).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

function typeIcon(type: string) {
  const cls = "w-5 h-5";
  switch (type) {
    case "friend_request":
      return <UserPlus className={`${cls} text-blue-400`} />;
    case "friend_accept":
      return <Users className={`${cls} text-emerald-400`} />;
    case "friend_vibe":
      return <MapPin className={`${cls} text-violet-400`} />;
    case "venue_busy":
      return <Flame className={`${cls} text-orange-400`} />;
    case "venue_quiet":
      return <Moon className={`${cls} text-cyan-400`} />;
    case "media_reaction":
      return <ThumbsUp className={`${cls} text-amber-400`} />;
    case "media_removed":
      return <Trash2 className={`${cls} text-red-400`} />;
    default:
      return <Bell className={`${cls} text-zinc-400`} />;
  }
}

interface NotificationsPanelProps {
  onSelect: (link: string) => void;
}

export function NotificationsPanel({ onSelect }: NotificationsPanelProps) {
  const { data, isLoading } = useNotifications();
  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  const markRead = useMutation({
    mutationFn: (id: number) => apiRequest("POST", `/api/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => apiRequest("POST", "/api/notifications/read-all"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  const handleTap = (n: SelectNotification) => {
    if (!n.read) markRead.mutate(n.id);
    if (n.link) onSelect(n.link);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-6 pt-2 pb-4 flex items-center justify-between flex-shrink-0">
        <h2 className="text-white font-display font-bold text-xl" data-testid="text-notifications-title">
          Notifications
        </h2>
        {unreadCount > 0 && (
          <button
            data-testid="button-mark-all-read"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <CheckCheck className="w-4 h-4" />
            Mark all read
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-6 space-y-2">
        {isLoading ? (
          <div className="space-y-2" data-testid="status-notifications-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 rounded-2xl bg-zinc-900 animate-pulse" />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center text-center py-20"
            data-testid="empty-notifications"
          >
            <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
              <Bell className="w-8 h-8 text-zinc-500" />
            </div>
            <h3 className="text-white font-display font-bold text-lg mb-1">No notifications yet</h3>
            <p className="text-zinc-500 text-sm max-w-xs">
              Friend requests, vibe alerts, and reactions to your posts will show up here.
            </p>
          </div>
        ) : (
          notifications.map((n) => (
            <button
              key={n.id}
              data-testid={`row-notification-${n.id}`}
              onClick={() => handleTap(n)}
              className={`w-full flex items-start gap-3 p-3 rounded-2xl border text-left transition-all ${
                n.read
                  ? "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
                  : "bg-primary/10 border-primary/30 hover:border-primary/50"
              }`}
            >
              <div className="mt-0.5 flex-shrink-0">{typeIcon(n.type)}</div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm">{n.title}</p>
                <p className="text-zinc-400 text-sm line-clamp-2">{n.body}</p>
                <p className="text-zinc-600 text-xs mt-1">{relativeTime(n.createdAt)}</p>
              </div>
              {!n.read && (
                <span className="mt-1.5 w-2 h-2 rounded-full bg-primary flex-shrink-0" aria-label="unread" />
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
